import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeSimple, Eye, X } from '@phosphor-icons/react';
import { getContacts } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { toast } from 'sonner';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = async () => {
    try {
      const res = await getContacts();
      setContacts(res.data.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleView = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl text-secondary">Contact Submissions</h1>
        <p className="font-body text-sm text-text-secondary">{contacts.length} messages</p>
      </div>

      {/* Contacts List */}
      <div className="space-y-4">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="admin-card animate-pulse">
              <div className="h-20 bg-gray-100 rounded" />
            </div>
          ))
        ) : contacts.length === 0 ? (
          <div className="admin-card text-center py-12">
            <EnvelopeSimple size={48} className="text-text-muted mx-auto mb-4" />
            <p className="font-body text-text-secondary">No contact submissions yet</p>
          </div>
        ) : (
          contacts.map((contact) => (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="admin-card"
              data-testid={`contact-card-${contact.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-display text-lg text-secondary">{contact.name}</h3>
                    <span className="font-body text-xs text-text-muted">
                      {formatDate(contact.created_at)}
                    </span>
                  </div>
                  <p className="font-body text-sm text-primary mb-1">{contact.subject}</p>
                  <p className="font-body text-sm text-text-secondary line-clamp-2">
                    {contact.message}
                  </p>
                  <p className="font-body text-xs text-text-muted mt-2">{contact.email}</p>
                </div>
                
                <button
                  onClick={() => handleView(contact)}
                  data-testid={`view-contact-${contact.id}`}
                  className="p-2 text-text-muted hover:text-primary transition-colors flex-shrink-0"
                >
                  <Eye size={20} />
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* View Modal */}
      {showModal && selectedContact && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">Message Details</h3>
              <button onClick={() => setShowModal(false)}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <p className="font-body text-xs text-text-muted uppercase mb-1">From</p>
                <p className="font-body text-sm text-secondary">{selectedContact.name}</p>
                <a 
                  href={`mailto:${selectedContact.email}`}
                  className="font-body text-sm text-primary hover:underline"
                >
                  {selectedContact.email}
                </a>
              </div>
              
              <div>
                <p className="font-body text-xs text-text-muted uppercase mb-1">Subject</p>
                <p className="font-body text-sm text-secondary">{selectedContact.subject}</p>
              </div>
              
              <div>
                <p className="font-body text-xs text-text-muted uppercase mb-1">Message</p>
                <p className="font-body text-sm text-text-secondary whitespace-pre-wrap">
                  {selectedContact.message}
                </p>
              </div>
              
              <div>
                <p className="font-body text-xs text-text-muted uppercase mb-1">Received</p>
                <p className="font-body text-sm text-text-secondary">
                  {formatDate(selectedContact.created_at)}
                </p>
              </div>
              
              <div className="pt-4 border-t border-border-default">
                <a
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                  className="btn-primary w-full text-center block"
                >
                  Reply via Email
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
